import { memo, type ReactNode } from 'react';

import { type Notification } from '@air/api/types';
import { type NotificationsItemProps } from '@air/component-notifications';
import { CheckIcon, XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { formatActorNameInitialsOrDefault, formatFullName } from '@air/utilities';
import { convertUnknownToError } from '@air/utils-error';

import { NotificationBaseItem } from '~/components/AirNotifications/components/NotificationBaseItem';
import { useLibraryApproveMemberMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberApproveMutation';
import { useLibraryMembersDenyMutation } from '~/components/LibraryBeta/hooks/mutations/useLibraryMemberDenyMutation';
import { useLibraryToasts } from '~/hooks/useLibraryToasts';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

export type NotificationLibraryMemberRequestedTypeProps = Pick<NotificationsItemProps, 'onClick'> & {
  notification: Notification<'libraryMemberInvited_userIsAdmin', 'account'>;
  onApprove: () => void;
  onDeny: () => void;
};

export const NotificationLibraryMemberRequestedType = memo(
  ({ notification, onApprove, onClick, onDeny }: NotificationLibraryMemberRequestedTypeProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { showLibraryToast } = useLibraryToasts();
    const { libraryMembersDenyMutation } = useLibraryMembersDenyMutation({
      libraryId: notification.data.library.id,
    });
    const { libraryMemberApproveMutation } = useLibraryApproveMemberMutation({
      libraryId: notification.data.library.id,
    });

    /**
     * If the actor.id is not the same as the libraryMember.id, then we know that the
     * actor is inviting the libraryMember to the library.
     */
    const isMemberInvite = notification.data.actor.id !== notification.data.libraryMember.id;

    /**
     * Check if this is a pending request that needs approval or an informational notification
     * about a successful join (for open libraries)
     */
    const isPendingRequest = notification.data.libraryMember.pending;

    let joinActionText: ReactNode;

    if (!isPendingRequest) {
      joinActionText = ' has joined';
    } else if (isMemberInvite) {
      joinActionText = (
        <>
          {` has invited `}
          <b>
            {formatActorNameInitialsOrDefault({
              firstName: notification.data.libraryMember.firstName,
              lastName: notification.data.libraryMember.lastName,
            })}
          </b>
        </>
      );
    } else {
      joinActionText = ' has requested';
    }

    return (
      <NotificationBaseItem
        content={
          /**
           * We handle the showing and hiding of buttons based on the readAt property
-          * because we only mark the notifications as read when the user approves or
-          * denies the join request.
           * Only show approve/deny buttons for pending requests that haven't been read.
           * For non-pending (successful joins), don't show any action buttons.
           */
          isPendingRequest &&
          !notification.readAt && (
            <div className="relative z-10 flex gap-2">
              <Button
                appearance="outline"
                color="gray"
                isLoading={libraryMembersDenyMutation.isPending}
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                onClick={() => {
                  const fullName = formatFullName(
                    notification.data.libraryMember.firstName,
                    notification.data.libraryMember.lastName,
                  );

                  libraryMembersDenyMutation.mutate([{ accountId: notification.data.libraryMember.id }], {
                    onError: (error) =>
                      showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                    onSuccess: () => {
                      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
                      onDeny?.();
                      showLibraryToast({
                        message: (
                          <>
                            Denied <b className="ads:text-accent text-jay-200">{fullName}</b> join request.
                          </>
                        ),
                      });
                    },
                  });
                }}
                prefix={<XIcon size="xs" />}
                size="small"
              >
                Deny
              </Button>
              <Button
                appearance="outline"
                color="gray"
                isLoading={libraryMemberApproveMutation.isPending}
                // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
                onClick={() => {
                  const fullName = formatFullName(
                    notification.data.libraryMember.firstName,
                    notification.data.libraryMember.lastName,
                  );

                  libraryMemberApproveMutation.mutate(
                    { accountId: notification.data.libraryMember.id },
                    {
                      onError: (error) =>
                        showLibraryToast({ message: convertUnknownToError(error).message, type: 'error' }),
                      onSuccess: () => {
                        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
                        onApprove?.();
                        showLibraryToast({
                          message: (
                            <>
                              Approved <b className="ads:text-accent text-jay-200">{fullName}</b> join request.
                            </>
                          ),
                        });
                      },
                    },
                  );
                }}
                prefix={<CheckIcon size="xs" />}
                size="small"
              >
                Approve
              </Button>
            </div>
          )
        }
        meta={!!currentWorkspace?.name && `in ${currentWorkspace?.name}`}
        notification={notification}
        onClick={notification.readAt || !isPendingRequest ? onClick : undefined}
        title={
          <>
            <b>{formatActorNameInitialsOrDefault(notification.data.actor)}</b>

            {joinActionText}
            {` ${isPendingRequest ? 'to join' : ''} `}
            <b>{notification.data.library.title}</b>
          </>
        }
      />
    );
  },
);

NotificationLibraryMemberRequestedType.displayName = 'NotificationLibraryMemberRequestedType';
